import { Checkbox } from '@ibsys/kit';
import { format, parse, parseISO } from 'date-fns';
import type { ConversationAttributeItem } from '@/entities/attributes';
import type {
  ConversationAttrLeaf,
  ConversationAttrValue,
  ConversationAttrValueObject,
} from '@/entities/conversations';

/** Проверка, что узел — лист с примитивным значением (для type narrowing) */
export const isConversationAttrLeaf = (v: ConversationAttrValue): v is ConversationAttrLeaf =>
  'attributeValue' in v &&
  (v.attributeValue === null ||
    typeof v.attributeValue === 'string' ||
    typeof v.attributeValue === 'number' ||
    typeof v.attributeValue === 'boolean' ||
    Array.isArray(v.attributeValue));
import { getAttributeTimeFormat } from '@/entities/attributes/model/lib/getAttributeDisplayName';
import { ATTRIBUTE_TYPES, DISPLAY_FORMATS } from '@/entities/attributes/model/constants';
import { formatTimeInterval, formatTextTypography } from '@/shared/lib/utils/string';
import type { TimeIntervalFormat } from '@/shared/lib/utils/string';

/**
 * Получает значение атрибута по пути из вложенной структуры conversationAttrValues
 * @param attrValues - объект с атрибутами диалога
 * @param path - путь к атрибуту (например "speechAnalytics.parallelSpeech.maxLength")
 * @returns объект ConversationAttrValue или undefined если не найден
 */
export const getAttrValueByPath = (
  attrValues: ConversationAttrValueObject,
  path: string,
): ConversationAttrValue | undefined => {
  const parts = path.split('.');
  let current: ConversationAttrValue | ConversationAttrValueObject | undefined = attrValues;

  for (let i = 0; i < parts.length; i++) {
    if (!current || typeof current !== 'object') {
      return undefined;
    }
    const part = parts[i];
    const record = current as Record<string, ConversationAttrValue>;

    if (part in record) {
      current = record[part];
      continue;
    }

    // Ключ не найден — проверяем индексированные варианты (policy[0], policy[1], ...)
    const indexedKeys = Object.keys(record)
      .filter((k) => k.startsWith(`${part}[`))
      .sort();

    if (indexedKeys.length === 0) {
      return undefined;
    }

    // Собираем значения из всех индексированных элементов по оставшемуся пути
    const remainingPath = parts.slice(i + 1).join('.');
    const values: string[] = [];

    for (const key of indexedKeys) {
      const indexed = record[key];
      if (!indexed || typeof indexed !== 'object') continue;

      const leaf = remainingPath
        ? getAttrValueByPath(indexed as unknown as ConversationAttrValueObject, remainingPath)
        : indexed;

      if (leaf && 'attributeValue' in leaf && leaf.attributeValue != null) {
        values.push(String(leaf.attributeValue));
      }
    }

    // Убираем дубликаты, сохраняя порядок
    const unique = [...new Set(values)];

    if (unique.length === 0) {
      return undefined;
    }

    // Возвращаем синтетический лист с объединёнными значениями
    return {
      attributeName: parts[parts.length - 1],
      attributeValue: unique.join(', '),
    } as ConversationAttrLeaf;
  }

  return current as ConversationAttrValue | undefined;
};

/**
 * Рендерит значение атрибута диалога
 * @param attrValue - объект со значением атрибута (содержит attributeName и attributeValue)
 * @param attribute - описание атрибута для определения типа
 * @param timeDisplayFormat - общесистемный формат интервалов времени; формат атрибута важнее
 */
export const renderConversationAttrValue = (
  attrValue: ConversationAttrValue | undefined,
  attribute?: ConversationAttributeItem,
  timeDisplayFormat: TimeIntervalFormat = 'TIME',
) => {
  if (!attrValue) {
    return null;
  }

  const value = isConversationAttrLeaf(attrValue) ? attrValue.attributeValue : undefined;

  if (value === undefined || value === null) {
    return null;
  }

  // На случай передачи группы — value не должен быть вложенным объектом
  if (typeof value === 'object' && !Array.isArray(value)) {
    return null;
  }

  // Для SELECT и MULTISELECT значения уже преобразованы бэкендом в названия
  if (attribute?.dataType === ATTRIBUTE_TYPES.SELECT || attribute?.dataType === ATTRIBUTE_TYPES.MULTISELECT) {
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    return String(value);
  }

  // Форматирование дат
  if (typeof value === 'string') {
    // Проверяем, это дата в формате YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return format(parse(value, 'yyyy-MM-dd', new Date()), 'dd.MM.yyyy');
    }
    // Проверяем, это timestamp
    if (/^\d{4}-\d{2}-\d{2}T/.test(value) || /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(value)) {
      try {
        return format(parseISO(value), 'dd.MM.yyyy HH:mm');
      } catch {
        return value;
      }
    }
    return formatTextTypography(value);
  }

  // Boolean
  if (typeof value === 'boolean') {
    return <Checkbox value={value} disabled />;
  }

  // Интервалы времени — по действующему формату (формат атрибута важнее общесистемного)
  const timeFormat = getAttributeTimeFormat(attribute, timeDisplayFormat);
  if (typeof value === 'number' && timeFormat) {
    return formatTimeInterval(value, timeFormat);
  }

  if (typeof value === 'number' && attribute?.displayFormat === DISPLAY_FORMATS.PERCENT) {
    return `${value}%`;
  }

  // Числа: разбивка по разрядам для INTEGER и DECIMAL
  if (
    typeof value === 'number' &&
    (attribute?.dataType === ATTRIBUTE_TYPES.INTEGER || attribute?.dataType === ATTRIBUTE_TYPES.DECIMAL)
  ) {
    return new Intl.NumberFormat('ru-RU').format(value);
  }

  return value;
};

/**
 * Рендерит «сырое» строковое значение атрибута диалога, пришедшее с эндпоинта
 * `GET /dashboard/quality-details` (поле `attributes: Record<string, string | null>`),
 * приводя его к человекочитаемому виду по `attribute.dataType` / `displayFormat`.
 * Поведение унифицировано с гридом диалогов (см. {@link renderConversationAttrValue}).
 *
 * Используется в `AllDetailsCard` дашборда качества: бэк отдаёт значения
 * атрибутов как строки (`CHECKBOX` → `"true"`/`"false"`, `INTEGER` → `"42"`,
 * `DATE` → `"YYYY-MM-DD"` и т. д.) — фронт сам форматирует визуально.
 *
 * Пустое значение (включая `CHECKBOX === "false"`) возвращается как `null` —
 * ячейка отрисовывается пустой, без символа-заглушки. Унифицировано с гридом
 * диалогов (см. {@link renderConversationAttrValue}).
 */
export const renderQualityAttributeValue = (
  raw: string | null | undefined,
  attribute?: ConversationAttributeItem,
  timeDisplayFormat: TimeIntervalFormat = 'TIME',
) => {
  if (raw === null || raw === undefined || raw === '') {
    return null;
  }

  switch (attribute?.dataType) {
    case ATTRIBUTE_TYPES.CHECKBOX: {
      if (raw === 'true') return <Checkbox value disabled />;
      if (raw === 'false') return null;
      return raw;
    }
    case ATTRIBUTE_TYPES.TIME:
    case ATTRIBUTE_TYPES.INTEGER:
    case ATTRIBUTE_TYPES.DECIMAL: {
      const n = Number(raw);
      if (!Number.isFinite(n)) return raw;
      const timeFormat = getAttributeTimeFormat(attribute, timeDisplayFormat);
      if (timeFormat) {
        return formatTimeInterval(n, timeFormat);
      }
      if (attribute?.displayFormat === DISPLAY_FORMATS.PERCENT) {
        return `${n}%`;
      }
      return new Intl.NumberFormat('ru-RU').format(n);
    }
    case ATTRIBUTE_TYPES.DATE: {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
      try {
        return format(parse(raw, 'yyyy-MM-dd', new Date()), 'dd.MM.yyyy');
      } catch {
        return raw;
      }
    }
    case ATTRIBUTE_TYPES.TIMESTAMP: {
      try {
        return format(parseISO(raw), 'dd.MM.yyyy HH:mm');
      } catch {
        return raw;
      }
    }
    default:
      // SELECT, MULTISELECT, TEXT и неизвестные типы — бэк уже разрешил подписи / нормализовал
      return raw;
  }
};
